import styled from 'styled-components';
import { LucideIcon } from "lucide-react";

interface ServiceCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
}

const ServiceCard = ({ icon: Icon, title, description, features }: ServiceCardProps) => {
  return (
    <StyledWrapper>
      <div className="parent">
        <div className="card">
          <div className="logo">
            <span className="circle circle1" />
            <span className="circle circle2" />
            <span className="circle circle3" />
            <span className="circle circle4" />
            <span className="circle circle5">
              <Icon className="icon" />
            </span>
          </div>
          <div className="glass" />
          <div className="content">
            <span className="title">{title}</span>
            <span className="text">{description}</span>
          </div>
          <div className="bottom">
            <div className="features">
              {features.slice(0, 3).map((feature, index) => (
                <span key={index} className="feature">{feature}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .parent {
    width: 400px;
    height: 450px;
    perspective: 1000px;
  }

  .card {
    height: 100%;
    border-radius: 50px;
    background: linear-gradient(135deg, hsl(195, 100%, 50%) 0%, hsl(270, 100%, 60%) 100%);
    transition: all 0.5s ease-in-out;
    transform-style: preserve-3d;
    box-shadow: rgba(0, 212, 255, 0) 40px 50px 25px -40px, rgba(0, 212, 255, 0.2) 0px 25px 25px -5px;
  }

  .glass {
    transform-style: preserve-3d;
    position: absolute;
    inset: 8px;
    border-radius: 55px;
    border-top-right-radius: 100%;
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.349) 0%, rgba(255, 255, 255, 0.815) 100%);
    /* -webkit-backdrop-filter: blur(5px);
    backdrop-filter: blur(5px); */
    transform: translate3d(0px, 0px, 25px);
    border-left: 1px solid white;
    border-bottom: 1px solid white;
    transition: all 0.5s ease-in-out;
  }

  .content {
    padding: 120px 70px 0px 40px;
    transform: translate3d(0, 0, 26px);
  }

  .content .title {
    display: block;
    color: hsla(0, 0%, 0%, 1.00);
    font-weight: 900;
    font-size: 24px;
  }

  .content .text {
    display: block;
    color: hsla(0, 0%, 3%, 0.80);
    font-size: 16px;
    margin-top: 20px;
    line-height: 1.4;
  }

  .bottom {
    padding: 15px 20px;
    transform-style: preserve-3d;
    position: absolute;
    bottom: 25px;
    left: 25px;
    right: 25px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transform: translate3d(0, 0, 26px);
  }

  .bottom .features {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .bottom .feature {
    font-size: 14px;
    color: hsla(0, 0%, 9%, 1.00);
    font-weight: 500;
  }

  .bottom .view-more {
    display: flex;
    align-items: center;
    width: 40%;
    justify-content: flex-end;
    transition: all 0.2s ease-in-out;
  }

  .bottom .view-more:hover {
    transform: translate3d(0, 0, 10px);
  }

  .bottom .view-more .view-more-button {
    background: none;
    border: none;
    color: hsl(270, 100%, 60%);
    font-weight: bolder;
    font-size: 14px;
  }

  .bottom .view-more .svg {
    fill: none;
    stroke: hsl(270, 100%, 60%);
    stroke-width: 3px;
    max-height: 18px;
  }

  .logo {
    position: absolute;
    right: 0;
    top: 0;
    transform-style: preserve-3d;
  }

  .logo .circle {
    display: block;
    position: absolute;
    aspect-ratio: 1;
    border-radius: 50%;
    top: 0;
    right: 0;
    box-shadow: rgba(100, 100, 111, 0.2) -10px 10px 20px 0px;
    -webkit-backdrop-filter: blur(5px);
    backdrop-filter: blur(1px);
    background: hsla(195, 100%, 50%, 0.2);
    transition: all 0.5s ease-in-out;
  }

  .logo .circle1 {
    width: 200px;
    transform: translate3d(0, 0, 20px);
    top: 10px;
    right: 10px;
  }

  .logo .circle2 {
    width: 165px;
    transform: translate3d(0, 0, 40px);
    top: 12px;
    right: 12px;
    -webkit-backdrop-filter: blur(1px);
    backdrop-filter: blur(1px);
    transition-delay: 0.4s;
  }

  .logo .circle3 {
    width: 130px;
    transform: translate3d(0, 0, 60px);
    top: 20px;
    right: 20px;
    transition-delay: 0.8s;
  }

  .logo .circle4 {
    width: 95px;
    transform: translate3d(0, 0, 80px);
    top: 28px;
    right: 28px;
    transition-delay: 1.2s;
  }

  .logo .circle5 {
    width: 60px;
    transform: translate3d(0, 0, 100px);
    top: 35px;
    right: 35px;
    display: grid;
    place-content: center;
    transition-delay: 1.6s;
  }

  .logo .circle5 .icon {
    width: 24px;
    height: 24px;
    color: white;
  }

  .parent:hover .card {
    transform: rotate3d(1, 1, 0, 30deg);
    box-shadow: rgba(0, 212, 255, 0.3) 30px 50px 25px -40px, rgba(0, 212, 255, 0.1) 0px 25px 30px 0px;
  }

  .parent:hover .card .logo .circle2 {
    transform: translate3d(0, 0, 60px);
  }

  .parent:hover .card .logo .circle3 {
    transform: translate3d(0, 0, 80px);
  }

  .parent:hover .card .logo .circle4 {
    transform: translate3d(0, 0, 100px);
  }

  .parent:hover .card .logo .circle5 {
    transform: translate3d(0, 0, 120px);
  }
`;

export default ServiceCard;
